import { Container, Row, Col, Button } from 'react-bootstrap';
import React, { useState, useEffect } from 'react';

import SEO from '@/components/common/SEO';
import mainImage from '@/assets/img/spotlights/mainImage.avif';
import coloradoRiverImg from '@/assets/img/spotlights/coloradoRiver.jpg';
import susEnergyImg from '@/assets/img/spotlights/susEnergy.jpg';
import fireFightImg from '@/assets/img/spotlights/fireFight.jpg';
import dataCenterImg from '@/assets/img/spotlights/dataCenter.jpg';
import spotlightData from '@/assets/spotlights/spotlights.json';
import logger from '@/shared/utils/logger';

/**
 * Static image map keyed by the relative asset path stored in spotlights.json.
 * Replaces dynamic require() which is fragile with bundlers.
 */
const SPOTLIGHT_IMAGES = {
    'img/spotlights/coloradoRiver.jpg': coloradoRiverImg,
    'img/spotlights/susEnergy.jpg': susEnergyImg,
    'img/spotlights/fireFight.jpg': fireFightImg,
    'img/spotlights/dataCenter.jpg': dataCenterImg,
};

import StatisticalCard from './components/StatisticalCard';
import SpotlightCard from './components/SpotlightCard';
import waterSpotlightData from './spotlightData/waterSpotlightData';
import wildfireSpotlightData from './spotlightData/wildfireSpotlightData';
import dataCentersFrontRangeSpotlightData from './spotlightData/dataCentersFrontRangeSpotlightData';
import './SpotlightLanding.css';

const ENGINE_BASE_URL = process.env.REACT_APP_ENGINE_URL;

/** Live spotlights that have full detail pages implemented. */
const LIVE_SPOTLIGHTS = new Set([
    'water',
    'wildfires',
    'data-centers-front-range',
]);

/**
 * Published dates keyed by spotlight URL slug. Sourced directly from each
 * spotlight's data file so the date on the landing card and the date on the
 * detail page always come from the same single source of truth.
 */
const SPOTLIGHT_PUBLISHED_DATES = {
    water: waterSpotlightData.publishedOn,
    wildfires: wildfireSpotlightData.publishedOn,
    'data-centers-front-range': dataCentersFrontRangeSpotlightData.publishedOn,
};

export default function SpotlightLanding() {
    const [activeTab, setActiveTab] = useState('environment');
    const [realStats, setRealStats] = useState(null);
    const [statsError, setStatsError] = useState(false);

    const { spotlights } = spotlightData;

    // Count total spotlight topics across all pillars (static JSON, stable)
    const spotlightCount = Object.values(spotlights).reduce(
        (sum, arr) => sum + arr.length,
        0
    );

    // Fetch real counts from public Engine API.
    // Uses Promise.allSettled so a single endpoint failure does not suppress the
    // other card or the static "Exploring" card.
    useEffect(() => {
        const controller = new AbortController();
        // Track whether the abort came from the timeout vs cleanup. This lets
        // us distinguish "endpoint took too long" (real failure -> show error)
        // from React 18 StrictMode's intentional double-mount cleanup-abort
        // (transient -> bail silently and let the second mount run fresh).
        let timedOut = false;
        const timeoutId = setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, 12000);

        async function fetchStats() {
            const [sourcesResult, productsResult] = await Promise.allSettled([
                fetch(`${ENGINE_BASE_URL}/data/sources?status=active&limit=1`, {
                    signal: controller.signal,
                }),
                fetch(
                    `${ENGINE_BASE_URL}/data/products?status=active&limit=1`,
                    { signal: controller.signal }
                ),
            ]);

            // If aborted by cleanup (not timeout), bail without touching state.
            // In StrictMode the component will remount and run a fresh fetch.
            if (controller.signal.aborted && !timedOut) {
                return;
            }

            // Resolve each result independently; null means that endpoint failed.
            let sourceCount = null;
            if (
                sourcesResult.status === 'fulfilled' &&
                sourcesResult.value.ok
            ) {
                try {
                    const json = await sourcesResult.value.json();
                    sourceCount =
                        json?.pagination?.total ??
                        json?.data_sources?.length ??
                        null;
                } catch (parseErr) {
                    logger.error('Failed to parse sources response:', parseErr);
                }
            } else if (sourcesResult.status === 'rejected') {
                logger.error('Sources endpoint failed:', sourcesResult.reason);
            }

            let productCount = null;
            if (
                productsResult.status === 'fulfilled' &&
                productsResult.value.ok
            ) {
                try {
                    const json = await productsResult.value.json();
                    productCount =
                        json?.pagination?.total ??
                        json?.data_products?.length ??
                        null;
                } catch (parseErr) {
                    logger.error(
                        'Failed to parse products response:',
                        parseErr
                    );
                }
            } else if (productsResult.status === 'rejected') {
                logger.error(
                    'Products endpoint failed:',
                    productsResult.reason
                );
            }

            // Show the full error state when BOTH endpoints fail (covers the
            // timeout path - timedOut === true gets here). On any success,
            // also clear any prior error so a StrictMode re-mount that
            // succeeds after a transient failure renders cleanly.
            if (sourceCount === null && productCount === null) {
                setStatsError(true);
            } else {
                setRealStats({ sourceCount, productCount });
                setStatsError(false);
            }

            clearTimeout(timeoutId);
        }

        fetchStats();

        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, []);

    // Build stat cards from real API data only.
    // While loading (realStats === null) we render skeleton placeholders so
    // no fabricated numbers are ever shown to users.
    //
    // When realStats is set but an individual count is null (that endpoint
    // failed), the card renders "\u2014" so the layout is preserved without
    // fabricating a number. The static "Exploring" card always renders because
    // it derives only from spotlightCount (computed from the bundled JSON).
    const statisticalData = realStats
        ? [
              {
                  id: 1,
                  title: 'Curated',
                  value:
                      realStats.sourceCount !== null
                          ? `${realStats.sourceCount}`
                          : '\u2014',
                  description:
                      'active data sources powering sustainability research and AI insights.',
                  showBadge: true,
                  bgColor: 'spotlight-yellow',
                  hoverTitle: 'Goal',
                  hoverDescription:
                      'Grow to 100+ curated data sources spanning water, energy, wildfire, and more.',
              },
              {
                  id: 2,
                  title: 'Published',
                  value:
                      realStats.productCount !== null
                          ? `${realStats.productCount}`
                          : '\u2014',
                  description:
                      'data products including interactive maps, dashboards, and reports.',
                  showBadge: true,
                  bgColor: 'spotlight-primary',
                  hoverTitle: 'Goal',
                  hoverDescription:
                      'Produce 50+ data products connecting communities with actionable environmental data.',
              },
              {
                  id: 3,
                  title: 'Exploring',
                  value: `${spotlightCount}`,
                  description:
                      'spotlight topics shaping Colorado’s sustainable future across all pillars.',
                  showBadge: true,
                  bgColor: 'spotlight-yellow',
                  hoverTitle: 'Goal',
                  hoverDescription:
                      'Launch 10+ deep-dive spotlights covering environmental, social, and economic sustainability.',
              },
          ]
        : null;

    return (
        <>
            <SEO
                title="Spotlights"
                description="Featured sustainability topics and in-depth explorations."
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Spotlights', path: '/spotlights' },
                ]}
            />
            <section id="spotlight-landing" className="bg-dark min-vh-100">
                <Container fluid className="py-5">
                    <Row className="justify-content-center">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            <h1 className="spotlight-title fw-semibold text-center display-3 mb-5">
                                Spotlights
                            </h1>
                            <p className="spotlight-description text-start fs-3 mb-5">
                                Spotlights highlights the stories, people, and
                                data shaping Colorado&apos;s sustainable future.
                                Each feature showcases innovation and
                                collaboration driving environmental, economic,
                                and social progress. These spotlights invite you
                                to explore how communities across Colorado are
                                creating real impact and building a more
                                resilient tomorrow.
                            </p>
                            <div className="text-center mb-5">
                                <img
                                    src={mainImage}
                                    alt="Sustainability spotlight featuring Colorado's environmental initiatives"
                                    className="spotlight-hero-image img-fluid"
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row className="justify-content-center mt-5 mb-5 statistical-cards-row">
                        <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                            {/*
                             * aria-live region: announces stat load transitions to
                             * screen readers (WCAG 2.1 AA). The visually-hidden
                             * message changes when loading completes or errors.
                             */}
                            <div
                                role="status"
                                aria-live="polite"
                                className="visually-hidden"
                            >
                                {statsError
                                    ? 'Data source and data product statistics are currently unavailable.'
                                    : statisticalData
                                      ? 'Spotlight statistics loaded.'
                                      : 'Loading spotlight statistics.'}
                            </div>
                            <Row className="g-2 g-md-5">
                                {statsError ? (
                                    /*
                                     * Both endpoints failed. Show the "Stats
                                     * unavailable" notice in place of the two API
                                     * cards, but ALWAYS render the static
                                     * "Exploring" card — it needs no API data.
                                     */
                                    <>
                                        <Col xs={8} sm={8} md={8} lg={8}>
                                            <p className="text-secondary fst-italic text-center py-4 mb-0">
                                                Stats unavailable
                                            </p>
                                        </Col>
                                        <Col xs={4} sm={4} md={4} lg={4}>
                                            <StatisticalCard
                                                title="Exploring"
                                                value={`${spotlightCount}`}
                                                description="spotlight topics shaping Colorado’s sustainable future across all pillars."
                                                bgColor="spotlight-yellow"
                                                showBadge={true}
                                                hoverTitle="Goal"
                                                hoverDescription="Launch 10+ deep-dive spotlights covering environmental, social, and economic sustainability."
                                            />
                                        </Col>
                                    </>
                                ) : statisticalData ? (
                                    statisticalData.map((stat) => (
                                        <Col
                                            key={stat.id}
                                            xs={4}
                                            sm={4}
                                            md={4}
                                            lg={4}
                                        >
                                            <StatisticalCard
                                                title={stat.title}
                                                value={stat.value}
                                                description={stat.description}
                                                bgColor={stat.bgColor}
                                                showBadge={stat.showBadge}
                                                hoverTitle={stat.hoverTitle}
                                                hoverDescription={
                                                    stat.hoverDescription
                                                }
                                            />
                                        </Col>
                                    ))
                                ) : (
                                    /* Loading: 3 skeletons tinted to hint at the eventual card colors,
                                     * still clearly loading (animated shimmer, no text). */
                                    [
                                        'spotlight-yellow',
                                        'spotlight-primary',
                                        'spotlight-yellow',
                                    ].map((tint, idx) => (
                                        <Col
                                            key={idx}
                                            xs={4}
                                            sm={4}
                                            md={4}
                                            lg={4}
                                        >
                                            <div
                                                className="statistical-card-skeleton h-100"
                                                style={{
                                                    '--skeleton-tint': `var(--${tint})`,
                                                }}
                                                aria-hidden="true"
                                            />
                                        </Col>
                                    ))
                                )}
                            </Row>
                        </Col>
                    </Row>
                </Container>

                <section className="spotlight-why-section py-5">
                    <Container fluid>
                        <Row className="justify-content-center">
                            <Col xs={11} md={11} lg={11} xl={11} xxl={9}>
                                <h2 className="display-5 fw-semibold mb-4 spotlight-why-title">
                                    Why Spotlights?
                                </h2>
                                <p className="fs-5 mb-4 spotlight-why-text">
                                    Colorado faces interconnected environmental
                                    challenges. Statewide temperatures have{' '}
                                    <a
                                        href="https://climate.colostate.edu/drought_info.html"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        risen 2.3&deg;F since 1980
                                    </a>
                                    , the{' '}
                                    <a
                                        href="https://climate.colorado.gov/health-and-environmental-impacts"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        five most destructive wildfires have all
                                        occurred since 2012
                                    </a>
                                    , and the Denver metro area has been
                                    classified as a{' '}
                                    <a
                                        href="https://cdphe.colorado.gov/nonattainment-federal-ozone-pollution-standards"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        &ldquo;severe&rdquo; ozone nonattainment
                                        area
                                    </a>
                                    . Meanwhile, the state faces a projected{' '}
                                    <a
                                        href="https://cwcb.colorado.gov/colorado-water-plan/technical-update-to-the-plan"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        water supply gap of up to 560,000
                                        acre-feet per year by 2050
                                    </a>
                                    .
                                </p>
                                <p className="fs-5 mb-4 spotlight-why-text">
                                    Spotlights turn complex environmental data
                                    into accessible, explorable stories. Each
                                    one curates{' '}
                                    <strong>authoritative data sources</strong>{' '}
                                    from agencies like the USGS, CWCB, and NOAA,
                                    pairs them with{' '}
                                    <strong>
                                        interactive tools and visualizations
                                    </strong>
                                    , and lets you ask questions through{' '}
                                    <strong>Bili</strong>, our AI research
                                    assistant. Research shows that environmental
                                    education programs focusing on local issues
                                    and real data are effective at inspiring
                                    lasting behavioral change (
                                    <a
                                        href="https://naaee.org/programs/eeworks"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        NAAEE/Stanford
                                    </a>
                                    ).
                                </p>
                                <p className="fs-5 mb-0 spotlight-why-text">
                                    Whether you&apos;re a researcher, student,
                                    policymaker, or concerned citizen,
                                    spotlights give you the tools to understand
                                    what&apos;s happening in your community, and
                                    what you can do about it.
                                </p>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <section className="bg-light py-5">
                    <Container fluid>
                        <Row className="justify-content-center">
                            <Col
                                xs={11}
                                md={11}
                                lg={11}
                                xl={11}
                                xxl={9}
                                className="text-center"
                            >
                                <div className="d-flex justify-content-center mb-4">
                                    <div
                                        className="d-inline-flex p-2 gap-2 spotlight-tab-container"
                                        role="tablist"
                                        aria-label="Sustainability pillars"
                                    >
                                        <Button
                                            role="tab"
                                            aria-selected={
                                                activeTab === 'environment'
                                            }
                                            onClick={() =>
                                                setActiveTab('environment')
                                            }
                                            className={`px-3 py-2 fs-5 border-0 spotlight-tab-btn ${
                                                activeTab === 'environment'
                                                    ? 'active'
                                                    : 'inactive'
                                            }`}
                                        >
                                            Environment
                                        </Button>
                                        <Button
                                            role="tab"
                                            aria-selected={
                                                activeTab === 'social'
                                            }
                                            onClick={() =>
                                                setActiveTab('social')
                                            }
                                            className={`px-3 py-2 fs-5 border-0 spotlight-tab-btn ${
                                                activeTab === 'social'
                                                    ? 'active'
                                                    : 'inactive'
                                            }`}
                                        >
                                            Social
                                        </Button>
                                        <Button
                                            role="tab"
                                            aria-selected={
                                                activeTab === 'economic'
                                            }
                                            onClick={() =>
                                                setActiveTab('economic')
                                            }
                                            className={`px-3 py-2 fs-5 border-0 spotlight-tab-btn ${
                                                activeTab === 'economic'
                                                    ? 'active'
                                                    : 'inactive'
                                            }`}
                                        >
                                            Economic
                                        </Button>
                                    </div>
                                </div>

                                <div
                                    className="mt-5 pt-5"
                                    role="tabpanel"
                                    aria-label={`${activeTab} spotlights`}
                                >
                                    {spotlights[activeTab].length > 0 ? (
                                        spotlights[activeTab].map(
                                            (spotlight, index) => {
                                                const slug = spotlight.link
                                                    ?.split('/')
                                                    .pop();
                                                return (
                                                    <React.Fragment
                                                        key={spotlight.id}
                                                    >
                                                        <SpotlightCard
                                                            title={
                                                                spotlight.title
                                                            }
                                                            description={
                                                                spotlight.description
                                                            }
                                                            imgSrc={
                                                                SPOTLIGHT_IMAGES[
                                                                    spotlight
                                                                        .imgSrc
                                                                ]
                                                            }
                                                            imgAlt={
                                                                spotlight.imgAlt
                                                            }
                                                            link={
                                                                spotlight.link
                                                            }
                                                            comingSoon={
                                                                !LIVE_SPOTLIGHTS.has(
                                                                    slug
                                                                )
                                                            }
                                                            publishedOn={
                                                                SPOTLIGHT_PUBLISHED_DATES[
                                                                    slug
                                                                ]
                                                            }
                                                        />
                                                        {index <
                                                            spotlights[
                                                                activeTab
                                                            ].length -
                                                                1 && (
                                                            <hr className="spotlight-divider" />
                                                        )}
                                                    </React.Fragment>
                                                );
                                            }
                                        )
                                    ) : (
                                        <p className="text-dark fs-5 text-center">
                                            {activeTab.charAt(0).toUpperCase() +
                                                activeTab.slice(1)}{' '}
                                            content coming soon
                                        </p>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </section>
        </>
    );
}
